import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import '../App.css'
import { GradientContainer } from '../components'
import { useAuth } from '../context/authContext'
import useLenisScroll from '../components/lenisScroller'

// Types - CORRECTED to match Firebase field names
interface Project {
  id: string
  collection: 'software' | 'aiprojects'
  title: string
  description?: string
  dateCreated?: any // Firebase Timestamp or Date
  technologies?: string[]
  features?: string[]
  githubURL?: string    // Changed from githubUrl
  websiteURL?: string   // Changed from liveUrl - removed demoUrl
  content?: string
}

interface ProjectItemProps {
  project: Project
  number: string
  onClick: () => void
}

const ProjectItem: React.FC<ProjectItemProps> = ({ project, number, onClick }) => {
  return (
    <div onClick={onClick} className="flex gap-3 cursor-pointer transition-all duration-300 ease-in-out hover:text-blue-400">
      <span className='text-sm mt-auto'>{number}</span>
      <p className="text-2xl md:text-4xl lg:text-5xl font-stretchRegular text-right w-full leading-tight">
        {project.title}
      </p>
    </div>
  )
}

const SoftwareProjects: React.FC = () => {
  const navigate = useNavigate()
  const { projectsLoading, getProjectsByCollection } = useAuth()
  
  // Mobile detection hook
  const [isMobile, setIsMobile] = useState(false)
  
  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768)
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])
  
  // Always call the hook, but conditionally use it
  const lenisScrollRef = useLenisScroll()
  const scrollRef = isMobile ? null : lenisScrollRef
  
  const softwareProjects: Project[] = getProjectsByCollection('software')

  const handleProjectClick = (project: Project): void => {
    const projectSlug = project.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
    
    navigate(`/portfolio/${projectSlug}`, { 
      state: { project } 
    })
  }

  if (projectsLoading) {
    return (
      <div className='text-white m-4 md:m-8 h-full pt-16 md:pt-8'>
        <h2 className="text-sm md:text-lg mb-6 pt-safe-top md:pt-0">Software Development Projects</h2>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Loading projects...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='text-white m-4 md:m-8 h-full pt-16 md:pt-8'>
      {/* Title */}
      <h2 className="text-sm md:text-lg mb-6 pt-safe-top md:pt-0">Software Development Projects</h2>
      
      {/* Content container with proper scrolling */}
      <div 
        ref={scrollRef}
        className="h-full pb-8 md:pb-20 overflow-y-auto overflow-x-hidden pb-safe-bottom hide-scrollbar"
        style={{ 
          height: 'calc(100% - 6rem)',
          WebkitOverflowScrolling: 'touch',
          maxHeight: 'calc(100dvh - 12rem)'
        }}
      >
        <div className="flex flex-col gap-4 md:gap-8">
          {softwareProjects.length === 0 ? (
            <div className="text-center text-gray-400 py-12">
              <p className="text-lg md:text-xl">No software projects found.</p>
              <p className="text-sm mt-2">Projects will appear here once added through the admin panel.</p>
            </div>
          ) : (
            softwareProjects.map((project: Project, index: number) => (
              <ProjectItem
                key={project.id || `software-${index}`}
                project={project}
                number={String(index + 1).padStart(2, '0')}
                onClick={() => handleProjectClick(project)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

const AIProjects: React.FC = () => {
  const navigate = useNavigate()
  const { projectsLoading, getProjectsByCollection } = useAuth()
  
  // Mobile detection hook
  const [isMobile, setIsMobile] = useState(false)
  
  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768)
    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])
  
  // Always call the hook, but conditionally use it
  const lenisScrollRef = useLenisScroll()
  const scrollRef = isMobile ? null : lenisScrollRef
  
  const aiProjects: Project[] = getProjectsByCollection('aiprojects')

  const handleProjectClick = (project: Project): void => {
    const projectSlug = project.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
    
    navigate(`/portfolio/${projectSlug}`, { 
      state: { project } 
    })
  }

  if (projectsLoading) {
    return (
      <div className='text-white m-4 md:m-8 h-full pt-16 md:pt-8'>
        <h2 className="text-sm md:text-lg mb-6 pt-safe-top md:pt-0">Artificial Intelligence Projects</h2>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Loading projects...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='text-white m-4 md:m-8 h-full pt-16 md:pt-8'>
      {/* Title */}
      <h2 className="text-sm md:text-lg mb-6 pt-safe-top md:pt-0">Artificial Intelligence Projects</h2>
      
      {/* Content container with proper scrolling */}
      <div 
        ref={scrollRef}
        className="h-full pb-8 md:pb-20 overflow-y-auto overflow-x-hidden pb-safe-bottom hide-scrollbar"
        style={{ 
          height: 'calc(100% - 6rem)',
          WebkitOverflowScrolling: 'touch',
          maxHeight: 'calc(100dvh - 12rem)'
        }}
      >
        <div className="flex flex-col gap-4 md:gap-8">
          {aiProjects.length === 0 ? (
            <div className="text-center text-gray-400 py-12">
              <p className="text-lg md:text-xl">No AI projects found.</p>
              <p className="text-sm mt-2">Projects will appear here once added through the admin panel.</p>
            </div>
          ) : (
            aiProjects.map((project: Project, index: number) => (
              <ProjectItem
                key={project.id || `ai-${index}`}
                project={project}
                number={String(index + 1).padStart(2, '0')}
                onClick={() => handleProjectClick(project)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// Mobile section switcher component
const MobilePortfolioSwitcher: React.FC = () => {
  const [currentSection, setCurrentSection] = useState(0)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  const sections = [
    { component: <SoftwareProjects key="software" />, name: "Software" },
    { component: <AIProjects key="ai" />, name: "AI Projects" }
  ]

  const nextSection = () => {
    setCurrentSection((prev) => (prev + 1) % sections.length)
  }

  const prevSection = () => {
    setCurrentSection((prev) => (prev - 1 + sections.length) % sections.length)
  }

  // Touch handlers for swipe functionality
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50
    
    if (isLeftSwipe) {
      nextSection()
    } else if (isRightSwipe) {
      prevSection()
    }
  }

  return (
    <div 
      className="h-full relative"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-20"
           style={{ top: 'max(1rem, env(safe-area-inset-top))' }}>
        {sections.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSection(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentSection 
                ? 'bg-white' 
                : 'bg-white/40'
            }`}
          />
        ))}
      </div>

      {/* Navigation arrows */}
      <button
        onClick={prevSection}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 p-2 bg-white/10 rounded-full backdrop-blur-sm"
        style={{ left: 'max(1rem, env(safe-area-inset-left))' }}
      >
        <ChevronLeft className="text-white" size={20} />
      </button>
      
      <button
        onClick={nextSection}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 p-2 bg-white/10 rounded-full backdrop-blur-sm"
        style={{ right: 'max(1rem, env(safe-area-inset-right))' }}
      >
        <ChevronRight className="text-white" size={20} />
      </button>

      {/* Content */}
      <div className="h-full">
        {sections[currentSection].component}
      </div>
    </div>
  )
}

const Portfolio: React.FC = () => {
  return (
    <div className='bg-black p-4 md:p-8 flex flex-col'
         style={{
           height: '100dvh',
           minHeight: '100svh'
         }}>
      {/* Mobile Layout */}
      <div className="block md:hidden h-full">
        <GradientContainer 
          components={[
            <MobilePortfolioSwitcher key="mobile-switcher" />
          ]}
        />
      </div>

      {/* Desktop Layout - 2 Columns */}
      <div className="hidden md:block h-full">
        <GradientContainer 
          components={[
            <SoftwareProjects key="desktop-software" />,
            <AIProjects key="desktop-ai" />
          ]}
        />
      </div>

      <div className='absolute text-white opacity-30 text-xs'
           style={{
             bottom: 'max(0.5rem, env(safe-area-inset-bottom))',
             left: 'max(0.5rem, env(safe-area-inset-left))'
           }}>
        &copy; AHMED ELGHAMRINY
      </div>
    </div>
  )
}

export default Portfolio;